import React from "react";
import { FONT, TYPE } from "../../theme/tokens";

// The dashboard/workflow shell's equivalent of components/landing/LandingStyles.jsx —
// same TYPE/FONT tokens and component-class vocabulary (Eyebrow dot, headings,
// band-raised, plan-card-style selectable cards), scoped to .subscene-dashboard instead
// of .subscene-landing so the two stylesheets never collide. Kept as a separate file
// rather than widening LandingStyles.jsx's scope, since the frontend README documents that file
// as landing-page-specific and other pages import primitives.jsx/motion3d.jsx directly
// without it.
//
// Colors reference var(--color-*) (declared in src/theme/dashboardTheme.css,
// dark defaults; overridden for the light theme by src/theme/dashboardTheme.js
// — see App.jsx) rather than PAGE.* interpolated as static hex, so these rules
// respond to the dashboard's light/dark toggle automatically.
//
// Deliberately no keyframes or Framer-driven entrance animation here — the dashboard is
// revisited constantly rather than scrolled once, so it borrows the landing page's static
// look (tokens, card language, glow accents) without the scroll-triggered choreography.

const DashboardStyles = () => (
  <style>{`

    .subscene-dashboard .rule-top { border-top: 1px solid var(--color-line-soft); }
    .subscene-dashboard .band-raised {
      background: var(--color-ink-raised);
      border-top: 1px solid var(--color-line-soft);
      border-bottom: 1px solid var(--color-line-soft);
    }

    .subscene-dashboard .mono-micro { font-family: ${FONT.mono}; font-size: ${TYPE.micro}; letter-spacing: 0.10em; text-transform: uppercase; color: var(--color-mute); }
    .subscene-dashboard .mono-label { font-family: ${FONT.mono}; font-size: ${TYPE.label}; letter-spacing: 0.14em; text-transform: uppercase; color: var(--color-mute); }

    .subscene-dashboard .heading-lg { font-size: clamp(1.9rem, 4.4vw, 3rem); font-weight: 700; line-height: 1.06; letter-spacing: -0.015em; }
    .subscene-dashboard .heading-md { font-size: clamp(1.2rem, 2.7vw, 1.7rem); font-weight: 600; line-height: 1.1; letter-spacing: -0.01em; }
    .subscene-dashboard .lede { font-size: ${TYPE.body}; color: var(--color-mute); }

    /* Selectable option card — the format picker (long/short), the Home hero's
       two entry-point cards. --option-accent supplies the selected-state colour,
       volt by default, so the class stays static per LandingStyles' pattern. */
    /* Surface and border match .pillar-card on the guides hub (BlogStyles.jsx),
       so the two card languages are one. --color-line rather than
       --color-line-soft: the former is accent-tinted (volt at 20% dark / 30%
       light), the latter a neutral grey, and that tint is where the pillar
       card's warmth comes from. */
    .subscene-dashboard .option-card {
      position: relative;
      background: var(--color-panel);
      border: 1px solid var(--color-line);
      border-left-width: 2px;
      transition: border-color 0.15s ease, background-color 0.15s ease, transform 0.2s ease;
    }
    .subscene-dashboard .option-card.is-selected {
      border-color: var(--option-accent, var(--color-volt));
      background: color-mix(in srgb, var(--option-accent, var(--color-volt)) 6%, var(--color-panel));
    }

    /* The volt edge marker.
       
       Not a new invention: Sidebar.js:222 already marks the active nav item
       with a volt left edge, ghosted on hover. That is the dashboard's
       existing "this one is current" signal, so option cards adopt it rather
       than growing a third dialect. The guides hub's .pillar-card is the same
       idea, and this matches it exactly.
       
       A real border-left, NOT an inset pseudo-element: the border is painted on
       the same box as border-radius, so the colour sweeps around the top-left
       and bottom-left curves and reads as part of the card's edge. A floating
       bar inset from the corners is a visibly different thing — it reads as a
       stripe alongside the card.
       
       Every state keeps the same 2px width and changes only colour, so nothing
       reflows between them. Mixed against --color-panel, NOT --color-line: the
       line tokens are translucent (light's is 30% alpha), so mixing the accent
       into one yields a mostly-transparent edge — fine on dark, but nearly
       invisible against the light theme's pale panel.
       
       Colour comes from --option-accent, which Home sets inline per option
       (volt / signal) — previously inert there, since it was only ever read
       under .is-selected and Home's cards are navigation, never selected. */

    /* Ghost on hover = affordance; full = state. .has-accent is the always-on
       opt-in for navigation cards, which are destinations rather than states. */
    .subscene-dashboard .option-card:hover {
      border-left-color: color-mix(in srgb, var(--option-accent, var(--color-volt)) 55%, var(--color-panel));
    }
    .subscene-dashboard .option-card.has-accent {
      border-left-color: color-mix(in srgb, var(--option-accent, var(--color-volt)) 75%, var(--color-panel));
    }
    .subscene-dashboard .option-card.has-accent:hover,
    .subscene-dashboard .option-card.is-selected {
      border-left-color: var(--option-accent, var(--color-volt));
    }

    /* Primary action built on the option-card language: the five Generate
       buttons (ScreenWriter, SpeechGenerator, the three video workflows).
       
       Disabled it is a plain option card — panel surface, volt left edge — and
       once the form is valid it fills solid volt, so "this is ready to run"
       reads at a glance from across the page.
       
       Defined HERE rather than with Tailwind's bg-volt on the element: the
       .subscene-dashboard .option-card rule is two selectors (0,2,0) and beats
       a single utility class (0,1,0), so bg-volt silently lost and the enabled
       button kept the panel background with near-black text on it. */
    .subscene-dashboard .option-card.is-action:not(:disabled) {
      background: var(--color-volt);
      border-color: var(--color-volt);
      color: var(--color-ink);
    }

    /* A neon pulse travelling around the card's border, for an option that
       has to catch the eye (Web Crawl). The ring is a conic gradient masked
       down to the border; the second layer is the same ring blurred into a
       glow. Without @property support the ring sits still, which is fine. */
    @property --trace-angle {
      syntax: '<angle>';
      initial-value: 0deg;
      inherits: false;
    }
    .subscene-dashboard .neon-trace::before,
    .subscene-dashboard .neon-trace::after {
      content: '';
      position: absolute;
      inset: -1px;
      border-radius: inherit;
      padding: 1.5px;
      pointer-events: none;
      background: conic-gradient(
        from var(--trace-angle),
        transparent 0deg 250deg,
        color-mix(in srgb, var(--color-volt) 40%, transparent) 290deg,
        var(--color-volt) 335deg,
        #fff 350deg,
        var(--color-volt) 358deg,
        transparent 360deg
      );
      -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
      -webkit-mask-composite: xor;
      mask: linear-gradient(#000 0 0) content-box exclude, linear-gradient(#000 0 0);
      animation: subsceneTrace 3s linear infinite;
    }
    .subscene-dashboard .neon-trace::after {
      filter: blur(6px);
      opacity: 0.8;
    }
    @keyframes subsceneTrace {
      to { --trace-angle: 360deg; }
    }
    @media (prefers-reduced-motion: reduce) {
      .subscene-dashboard .neon-trace::before,
      .subscene-dashboard .neon-trace::after {
        animation: none;
      }
    }
    .subscene-dashboard .option-card.is-action:not(:disabled):hover {
      background: var(--color-volt-hover);
      border-color: var(--color-volt-hover);
    }
    .subscene-dashboard .option-card.is-action:disabled {
      cursor: not-allowed;
      color: var(--color-mute-dim);
    }

    /* Credits' "Most popular" package/subscription card. Static (the card is a
       <div> whose click target is a button inside it). Replaces a hardcoded
       rgba(198,255,58,...) glow that stayed the same neon volt in light mode,
       where it did not belong. */
    .subscene-dashboard .popular-card {
      border-left-width: 2px;
      border-left-color: var(--color-volt);
    }

    /* The eyebrow's right-hand marker. Hidden below md: the mobile nav trigger
       floats at top-right, exactly where this sits, and clipped it on every
       page ("START HERE" rendering as "STAR / HERE"). It restates what the
       left-hand label already says, so it is the half that gives way — the
       connecting rule stays, so the eyebrow still spans the full width.
       Scoped here rather than in primitives.jsx because Eyebrow is shared with
       the landing page, which has no floating trigger. */
    @media (max-width: 767px) {
      .subscene-dashboard .eyebrow-marker { display: none; }
    }

    .subscene-dashboard .eyebrow-dot {
      background: var(--eyebrow-accent, var(--color-volt));
      box-shadow: 0 0 12px var(--eyebrow-accent, var(--color-volt));
    }

    .subscene-dashboard .btn-volt {
      background: var(--color-volt);
      color: var(--color-ink);
      font-family: ${FONT.mono};
      letter-spacing: 0.12em;
      text-transform: uppercase;
      font-weight: 700;
    }

    .subscene-dashboard .card-glow { box-shadow: 0 0 40px rgba(var(--color-volt-rgb), 0.1); }

    /* VideoLibrary gallery tiles — shared with components/landing/LandingStyles.jsx's
       .subscene-landing rules, mirrored here so the gallery renders identically when
       reused on Home. Keep these two in sync if the tile visuals ever change. */

    /* The marquee's inner track is duplicated for a seamless loop and ends up
       ~4000px wide — LandingStyles.jsx clips it at the page root with
       overflow-x: clip (see that file for why "clip" and not "hidden": hidden
       forces the same box's untouched overflow-y to auto, which would turn
       this shell into an accidental vertical scroll container). That rule
       never reached this page, since Home mounts inside .subscene-dashboard,
       not .subscene-landing — so the wide track was inflating the whole
       document's horizontal scroll range instead of being contained. */
    .subscene-dashboard { overflow-x: clip; }

    .subscene-dashboard .media-corner { border-color: var(--corner-color, var(--color-volt)); }
    .subscene-dashboard .tile-card { border-color: var(--color-line-soft); background: var(--color-panel); }
    .subscene-dashboard .tile-media { aspect-ratio: 16 / 9; background: #000; }
    .subscene-dashboard .tile-play-button { background: rgba(8,9,10,0.6); border: 1px solid var(--color-line); }
    .subscene-dashboard .row-mask {
      mask-image: linear-gradient(90deg, transparent, #000 5%, #000 95%, transparent);
      -webkit-mask-image: linear-gradient(90deg, transparent, #000 5%, #000 95%, transparent);
    }

    @keyframes subsceneRail {
      from { transform: translate3d(0, 0, 0); }
      to   { transform: translate3d(-50%, 0, 0); }
    }
    @keyframes subsceneRailRtl {
      from { transform: translate3d(-50%, 0, 0); }
      to   { transform: translate3d(0, 0, 0); }
    }
    .subscene-dashboard .subscene-rail,
    .subscene-dashboard .subscene-rail-rtl {
      animation-timing-function: linear;
      animation-iteration-count: infinite;
      will-change: transform;
    }
    .subscene-dashboard .subscene-rail { animation-name: subsceneRail; }
    .subscene-dashboard .subscene-rail-rtl { animation-name: subsceneRailRtl; }
    .subscene-dashboard .group:hover .subscene-rail,
    .subscene-dashboard .group:hover .subscene-rail-rtl {
      animation-play-state: paused;
    }

    .subscene-dashboard ::selection {
      background: var(--color-volt);
      color: var(--color-ink);
    }
  `}</style>
);

export default DashboardStyles;
